import { useEffect, useRef, useState } from "react";
import type { ArtworkLayer, DesignSide, WearableConfig } from "../types";

interface Props {
  config: WearableConfig;
  selectedSide: DesignSide;
  textureCanvas: HTMLCanvasElement;
  /** Redraw signal: bump this number to force a preview refresh. */
  version: number;
  selectedLayerId: string | null;
  layers: ArtworkLayer[];
  onSelectLayer: (id: string | null) => void;
  onMoveLayer: (id: string, x: number, y: number) => void;
  onRotateLayer: (id: string, rotation: number) => void;
  onScaleLayer: (id: string, scale: number) => void;
}

const PREVIEW_SIZE = 360;
/** Pixels inside the selection rectangle treated as "on the border" → scale. */
const SCALE_BAND = 10;
/** Pixels outside the selection rectangle that count as "just outside" → rotate. */
const ROTATE_BAND = 28;
/** Absolute bounds applied to the scale slider during drag. */
const SCALE_MIN = 0.01;
const SCALE_MAX = 2;

type HoverZone = null | "move" | "scale" | "rotate";

/**
 * 2D editor preview. Shows the flattened texture scaled down and lets
 * the user:
 *   - Click+drag inside the selected layer's bbox (but away from its
 *     edges) to reposition it in texture space.
 *   - Click on the selection border (inside, within SCALE_BAND) and
 *     drag to scale uniformly from the layer's centre.
 *   - Click just outside the selection border (within ROTATE_BAND)
 *     and drag to rotate the layer around its centre.
 *
 * Active mode is signalled both by the cursor and by a small icon
 * drawn on the canvas (↕↔ for scale, ↻ for rotate).
 */
export function Canvas2DPreview(props: Props) {
  const {
    config,
    selectedSide,
    textureCanvas,
    version,
    selectedLayerId,
    layers,
    onSelectLayer,
    onMoveLayer,
    onRotateLayer,
    onScaleLayer,
  } = props;

  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const moveDragRef = useRef<{
    layerId: string;
    offsetX: number;
    offsetY: number;
  } | null>(null);
  const rotateDragRef = useRef<{
    layerId: string;
    cx: number;
    cy: number;
    startAngle: number;
    startRotation: number;
  } | null>(null);
  const scaleDragRef = useRef<{
    layerId: string;
    cx: number;
    cy: number;
    startDist: number;
    startScale: number;
  } | null>(null);

  const [hoverZone, setHoverZone] = useState<HoverZone>(null);
  const [pointerPos, setPointerPos] = useState<{ x: number; y: number } | null>(
    null,
  );

  const previewScale = PREVIEW_SIZE / config.textureSize;

  const selected = layers.find((l) => l.id === selectedLayerId) ?? null;
  const selectedVisibleOnSide =
    selected && selected.side === selectedSide && selected.visible
      ? selected
      : null;

  // Convert a pointer position into the selected layer's local coords
  // (axis-aligned, in preview pixels, with the layer centre at 0,0).
  const toLocal = (l: ArtworkLayer, px: number, py: number) => {
    const cx = l.x * previewScale;
    const cy = l.y * previewScale;
    const dx = px - cx;
    const dy = py - cy;
    const rad = (-l.rotation * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return {
      lx: dx * cos - dy * sin,
      ly: dx * sin + dy * cos,
      cx,
      cy,
    };
  };

  const layerHalfSize = (l: ArtworkLayer) => {
    const absScale = Math.abs(l.scale);
    return {
      halfW: (l.naturalWidth * absScale * previewScale) / 2,
      halfH: (l.naturalHeight * absScale * previewScale) / 2,
    };
  };

  // Classify a pointer position relative to the selected layer.
  function zoneForSelected(px: number, py: number): HoverZone {
    const l = selectedVisibleOnSide;
    if (!l) return null;
    const { lx, ly } = toLocal(l, px, py);
    const { halfW, halfH } = layerHalfSize(l);
    const insideBbox = Math.abs(lx) <= halfW && Math.abs(ly) <= halfH;
    if (insideBbox) {
      const distFromEdge = Math.min(halfW - Math.abs(lx), halfH - Math.abs(ly));
      if (distFromEdge <= SCALE_BAND) return "scale";
      return "move";
    }
    const outX = Math.max(0, Math.abs(lx) - halfW);
    const outY = Math.max(0, Math.abs(ly) - halfH);
    if (Math.hypot(outX, outY) <= ROTATE_BAND) return "rotate";
    return null;
  }

  // Redraw when signaled or when hover state changes.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(textureCanvas, 0, 0, PREVIEW_SIZE, PREVIEW_SIZE);

    // Selection outline + handle visuals.
    if (selectedVisibleOnSide) {
      const l = selectedVisibleOnSide;
      const absScale = Math.abs(l.scale);
      const w = l.naturalWidth * absScale * previewScale;
      const h = l.naturalHeight * absScale * previewScale;

      ctx.save();
      ctx.translate(l.x * previewScale, l.y * previewScale);
      ctx.rotate((l.rotation * Math.PI) / 180);

      // Dashed bbox. Brighten when hovering scale so the user can see
      // they're "on the selector".
      const scaleHover =
        hoverZone === "scale" || scaleDragRef.current !== null;
      ctx.strokeStyle = scaleHover
        ? "rgba(96, 165, 250, 1)"
        : "rgba(248, 113, 113, 0.95)";
      ctx.setLineDash([5, 3]);
      ctx.lineWidth = scaleHover ? 2 : 1.5;
      ctx.strokeRect(-w / 2, -h / 2, w, h);
      ctx.setLineDash([]);

      // Four small corner squares (scale handles) always visible when
      // selected, so users know the border is interactive.
      const halfW = w / 2;
      const halfH = h / 2;
      const corners: Array<[number, number]> = [
        [-halfW, -halfH],
        [halfW, -halfH],
        [halfW, halfH],
        [-halfW, halfH],
      ];
      for (const [cx, cy] of corners) {
        ctx.fillStyle = scaleHover ? "rgba(96, 165, 250, 1)" : "#0b1220";
        ctx.strokeStyle = "rgba(248, 113, 113, 0.95)";
        ctx.lineWidth = 1.25;
        ctx.beginPath();
        ctx.rect(cx - 4, cy - 4, 8, 8);
        ctx.fill();
        ctx.stroke();
      }
      ctx.restore();

      // Draw an icon near the pointer to signal active mode.
      const active =
        (scaleDragRef.current && "scale") ||
        (rotateDragRef.current && "rotate") ||
        hoverZone;
      if (pointerPos && (active === "scale" || active === "rotate")) {
        const iconOffset = 16; // nudge icon away from the pointer tip
        const ix = pointerPos.x + iconOffset;
        const iy = pointerPos.y - iconOffset;
        if (active === "scale") drawScaleIcon(ctx, ix, iy);
        else drawRotateIcon(ctx, ix, iy);
      }
    }

    // Side label.
    ctx.save();
    ctx.fillStyle = "rgba(255,255,255,0.65)";
    ctx.font = "11px system-ui, sans-serif";
    ctx.fillText(`Side: ${selectedSide.toUpperCase()}`, 8, 14);
    ctx.restore();
    // toLocal/layerHalfSize/zoneForSelected/selectedVisibleOnSide are
    // derived from deps below; safe to leave out of the list.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    config,
    selectedSide,
    textureCanvas,
    version,
    selectedLayerId,
    layers,
    hoverZone,
    pointerPos,
  ]);

  // Hit-test: which layer lies under the pointer? (ignores handles)
  function pickLayer(px: number, py: number): ArtworkLayer | null {
    for (let i = layers.length - 1; i >= 0; i--) {
      const l = layers[i];
      if (!l.visible || l.side !== selectedSide) continue;
      const { lx, ly } = toLocal(l, px, py);
      const { halfW, halfH } = layerHalfSize(l);
      if (Math.abs(lx) <= halfW && Math.abs(ly) <= halfH) {
        return l;
      }
    }
    return null;
  }

  function getLocal(e: React.PointerEvent<HTMLCanvasElement>) {
    const rect = e.currentTarget.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  }

  function normalizeDeg(d: number) {
    let x = d % 360;
    if (x > 180) x -= 360;
    if (x <= -180) x += 360;
    return x;
  }

  function onPointerDown(e: React.PointerEvent<HTMLCanvasElement>) {
    const { x, y } = getLocal(e);

    // If a layer is selected and the pointer is in its scale or rotate
    // zone, those take priority over picking a different layer.
    if (selectedVisibleOnSide) {
      const zone = zoneForSelected(x, y);
      const l = selectedVisibleOnSide;
      const cx = l.x * previewScale;
      const cy = l.y * previewScale;
      if (zone === "scale") {
        const startDist = Math.max(
          0.0001,
          Math.hypot(x - cx, y - cy),
        );
        scaleDragRef.current = {
          layerId: l.id,
          cx,
          cy,
          startDist,
          startScale: l.scale,
        };
        e.currentTarget.setPointerCapture(e.pointerId);
        return;
      }
      if (zone === "rotate") {
        rotateDragRef.current = {
          layerId: l.id,
          cx,
          cy,
          startAngle: Math.atan2(y - cy, x - cx),
          startRotation: l.rotation,
        };
        e.currentTarget.setPointerCapture(e.pointerId);
        return;
      }
    }

    // Otherwise fall through to pick/move behavior.
    const hit = pickLayer(x, y);
    if (!hit) {
      onSelectLayer(null);
      return;
    }
    onSelectLayer(hit.id);
    moveDragRef.current = {
      layerId: hit.id,
      offsetX: x - hit.x * previewScale,
      offsetY: y - hit.y * previewScale,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  }

  function onPointerMove(e: React.PointerEvent<HTMLCanvasElement>) {
    const { x, y } = getLocal(e);
    setPointerPos({ x, y });

    if (scaleDragRef.current) {
      const drag = scaleDragRef.current;
      const currDist = Math.hypot(x - drag.cx, y - drag.cy);
      const factor = currDist / drag.startDist;
      const sign = drag.startScale >= 0 ? 1 : -1;
      const mag = Math.abs(drag.startScale) * factor;
      const clamped = Math.max(SCALE_MIN, Math.min(SCALE_MAX, mag));
      onScaleLayer(drag.layerId, Number((sign * clamped).toFixed(4)));
      return;
    }

    if (rotateDragRef.current) {
      const drag = rotateDragRef.current;
      const angleNow = Math.atan2(y - drag.cy, x - drag.cx);
      const deltaDeg = ((angleNow - drag.startAngle) * 180) / Math.PI;
      const next = normalizeDeg(drag.startRotation + deltaDeg);
      onRotateLayer(drag.layerId, Math.round(next * 10) / 10);
      return;
    }

    if (moveDragRef.current) {
      const drag = moveDragRef.current;
      const nx = Math.max(
        0,
        Math.min(config.textureSize, (x - drag.offsetX) / previewScale),
      );
      const ny = Math.max(
        0,
        Math.min(config.textureSize, (y - drag.offsetY) / previewScale),
      );
      onMoveLayer(drag.layerId, Math.round(nx), Math.round(ny));
      return;
    }

    // Idle hover: update zone indicator for cursor + icon hints.
    const zone = zoneForSelected(x, y);
    if (zone !== hoverZone) setHoverZone(zone);
  }

  function onPointerUp(e: React.PointerEvent<HTMLCanvasElement>) {
    moveDragRef.current = null;
    rotateDragRef.current = null;
    scaleDragRef.current = null;
    try {
      e.currentTarget.releasePointerCapture(e.pointerId);
    } catch {
      /* ignore */
    }
  }

  function onPointerLeave() {
    if (
      !rotateDragRef.current &&
      !moveDragRef.current &&
      !scaleDragRef.current
    ) {
      setHoverZone(null);
      setPointerPos(null);
    }
  }

  const activeZone: HoverZone =
    (scaleDragRef.current && "scale") ||
    (rotateDragRef.current && "rotate") ||
    (moveDragRef.current && "move") ||
    hoverZone;
  const cursor =
    activeZone === "scale"
      ? "nwse-resize"
      : activeZone === "rotate"
        ? "grab"
        : activeZone === "move"
          ? "move"
          : "default";

  return (
    <div className="canvas2d-wrapper">
      <canvas
        ref={canvasRef}
        width={PREVIEW_SIZE}
        height={PREVIEW_SIZE}
        className="canvas2d"
        style={{ cursor }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        onPointerLeave={onPointerLeave}
      />
    </div>
  );
}

// -----------------------------------------------------------------
// Small canvas glyphs used to signal the active mode near the pointer.
// They're drawn in screen space (not layer-local) so they stay a
// constant, readable size regardless of layer scale or rotation.
// -----------------------------------------------------------------

function drawScaleIcon(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.save();
  // White pill background for contrast against any texture.
  roundRectFill(ctx, x - 12, y - 12, 24, 24, 6, "rgba(11, 18, 32, 0.9)");
  ctx.strokeStyle = "#93c5fd";
  ctx.lineWidth = 1.5;
  ctx.lineCap = "round";
  // Diagonal double-headed arrow (⤡).
  const r = 7;
  ctx.beginPath();
  ctx.moveTo(x - r, y - r);
  ctx.lineTo(x + r, y + r);
  ctx.stroke();
  const h = 3.5;
  ctx.beginPath();
  // Top-left arrow head.
  ctx.moveTo(x - r, y - r);
  ctx.lineTo(x - r + h, y - r);
  ctx.moveTo(x - r, y - r);
  ctx.lineTo(x - r, y - r + h);
  // Bottom-right arrow head.
  ctx.moveTo(x + r, y + r);
  ctx.lineTo(x + r - h, y + r);
  ctx.moveTo(x + r, y + r);
  ctx.lineTo(x + r, y + r - h);
  ctx.stroke();
  ctx.restore();
}

function drawRotateIcon(ctx: CanvasRenderingContext2D, x: number, y: number) {
  ctx.save();
  roundRectFill(ctx, x - 12, y - 12, 24, 24, 6, "rgba(11, 18, 32, 0.9)");
  ctx.strokeStyle = "#fca5a5";
  ctx.fillStyle = "#fca5a5";
  ctx.lineWidth = 1.5;
  ctx.lineCap = "round";
  const r = 7;
  // Open circular arrow: arc with a gap + arrowhead at its tip.
  const a0 = Math.PI * 0.25;
  const a1 = Math.PI * 1.85;
  ctx.beginPath();
  ctx.arc(x, y, r, a0, a1);
  ctx.stroke();
  const ex = x + r * Math.cos(a1);
  const ey = y + r * Math.sin(a1);
  ctx.save();
  ctx.translate(ex, ey);
  ctx.rotate(a1 + Math.PI / 2);
  ctx.beginPath();
  ctx.moveTo(0, 0);
  ctx.lineTo(-3.5, -5);
  ctx.lineTo(3.5, -5);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
  ctx.restore();
}

function roundRectFill(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
  fill: string,
) {
  ctx.save();
  ctx.fillStyle = fill;
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
}
